import React, { useState } from "react";
import { FiSend, FiCheckCircle } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";

const inputClass = "w-full px-4 py-3 rounded-lg bg-white/5 border border-navy-border text-white placeholder-gray-500 focus:outline-none focus:border-accent-light";
const empty = { name: "", email: "", phone: "", subject: "", message: "", website: "" };

// The Contact page's message form. Submissions are stored in the database and read in the admin
// panel under Contact > Messages. "website" is a hidden trap field for spam bots.
export default function ContactForm() {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");   // idle | sending | sent
  const [error, setError] = useState("");

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    const { ok, data } = await apiRequest("/core/contact-messages/", { method: "POST", body: form });
    if (!ok) {
      setStatus("idle");
      return setError(errorMessage(data));
    }
    setForm(empty);
    setStatus("sent");
  };

  return (
    <section id="contact-form" className="relative bg-navy-dark text-white py-14 md:py-20 scroll-mt-20">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-10">
          <span className="text-accent-light uppercase tracking-widest text-sm font-semibold">Contact Us</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight">Send us a message</h2>
          <p className="mt-3 text-gray-400">Tell us about your project or question and we will get back to you soon.</p>
        </div>

        <div className="rounded-xl border border-navy-border bg-navy p-6 md:p-8 shadow-[0_0_25px_rgba(34,211,238,0.12)]">
          {status === "sent" ? (
            <div className="text-center py-10">
              <FiCheckCircle className="mx-auto text-emerald-400" size={44} />
              <h3 className="mt-4 text-2xl font-bold">Thank you!</h3>
              <p className="mt-2 text-gray-400">Your message has been received. We will reply by email as soon as possible.</p>
              <button onClick={() => setStatus("idle")} className="mt-6 px-6 py-2.5 rounded-full border border-navy-border text-gray-200 hover:border-accent-light">
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <input required maxLength={150} placeholder="Your name *" className={inputClass} value={form.name} onChange={set("name")} />
                <input required type="email" placeholder="Email address *" className={inputClass} value={form.email} onChange={set("email")} />
                <input maxLength={40} placeholder="Phone (optional)" className={inputClass} value={form.phone} onChange={set("phone")} />
                <input maxLength={200} placeholder="Subject" className={inputClass} value={form.subject} onChange={set("subject")} />
              </div>
              <textarea required rows={6} placeholder="Your message *" className={`${inputClass} resize-y`} value={form.message} onChange={set("message")} />

              {/* Spam trap: invisible to people, filled in by bots */}
              <input tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={form.website} onChange={set("website")} className="hidden" />

              {error && <p className="text-sm text-red-400">{error}</p>}

              <button disabled={status === "sending"} className="w-full md:w-auto px-8 py-3 rounded-full bg-brand-gradient font-semibold inline-flex items-center justify-center gap-2 shadow-md shadow-accent/20 disabled:opacity-60">
                <FiSend /> {status === "sending" ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
